/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { IDialogContentStyleProps, IDialogContentStyles, IDialogStyleProps, IDialogStyles } from '@fluentui/react/lib/Dialog';
import { Image, ImageLoadState } from '@fluentui/react/lib/Image';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { Carousel, CarouselSlide } from 'src/core/components/Carousel';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';

const messages = defineMessages({
    gotIt: { id: 'clu.cluEntityCombinationOnboardingModal.gotIt', defaultMessage: 'Got it' },
    skip: { id: 'clu.cluEntityCombinationOnboardingModal.skip', defaultMessage: 'Skip tutorial' },
    firstSlideTitle: { id: 'clu.cluEntityCombinationOnboardingModal.firstSlideTitle', defaultMessage: 'Overlapping components' },
    secondSlideTitle: { id: 'clu.cluEntityCombinationOnboardingModal.secondSlideTitle', defaultMessage: 'Choose how an entity returns' },
    firstSlideContent: {
        id: 'clu.cluEntityCombinationOnboardingModal.firstSlideContent',
        defaultMessage: 'Sometimes multiple entity components are found to overlap within a piece of text.'
    },
    secondSlideContent: {
        id: 'clu.cluEntityCombinationOnboardingModal.secondSlideContent',
        defaultMessage: `You can select how you want the system to return each entity when it finds an overlap to remove confusion.
        Then when the system finds an overlap between components it will only return using this method.`
    }
});

const theme = getScopedTheme('CluEntityCombinationOnboardingModal');
const { styleDiv } = getStylistV2('CluEntityCombinationOnboardingModal');
const stackStyler = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>();

const slideContentStyles = stackStyler({ root: { textAlign: 'center', minHeight: 150, maxWidth: 580 } });
const slideHeaderStyles = stackStyler({ root: { minHeight: 220, backgroundColor: theme.slideHeaderBackgroundColor } });
const skipButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { color: theme.slidePrimaryTextColor } });
const modalStyles = classNamesFunction<IDialogStyleProps, IDialogStyles>()({
    main: { boxShadow: 'none', backgroundColor: 'transparent', width: 'auto' }
});
const dialogStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    topButton: { marginRight: '30px' },
    button: { color: `${theme.defaultColor} !important`, ':hover': { color: `${theme.slidePrimaryTextColor} !important` } }
});
const FooterContainer = styleDiv('FootContainer', {
    display: 'grid',
    paddingBottom: 10,
    justifyItems: 'end',
    gridTemplateColumns: '1fr auto'
});

const getSlideContent = (titleText: string, contentText: string, moreContent?: React.ReactNode) => {
    const slideTitleTextProps: LuisTextProps = { variant: 'xxLargePlus', color: theme.slidePrimaryTextColor };

    const slideContentContainerProps: IStackProps = {
        horizontalAlign: 'center',
        styles: slideContentStyles,
        tokens: { childrenGap: 8, padding: '24px 20px' }
    };

    return (
        <Stack {...slideContentContainerProps}>
            <EditableTitle titleTextProps={slideTitleTextProps} title={titleText} />
            <LuisText color={theme.slideTextColor}>{contentText}</LuisText>
            {moreContent}
        </Stack>
    );
};

export const CluEntityCombinationOnboardingModal = (props: BasicModalProps<object>) => {
    const { onDismiss } = props;
    const { localize } = useLocalization();

    const [isImageLoading, setIsImageLoading] = React.useState(false);
    const [currentSlideKey, setCurrentSlideKey] = React.useState('first');

    const getSlideHeader = (imageName: string) => (
        <Stack grow verticalAlign="center" horizontalAlign="center" styles={slideHeaderStyles}>
            <Image
                width="100%"
                src={getAssetsUrl(`assets/images/clu/${imageName}.svg`)}
                onLoadingStateChange={loadState => setIsImageLoading(loadState !== ImageLoadState.loaded)}
            />
            {isImageLoading && <Spinner size={SpinnerSize.large} />}
        </Stack>
    );

    const skipModal = () => {
        getStorage('localStorage').setData(LuisConstants.StorageKeys.CluEntityOverlapMethodTutorialSeen, 'true');
        onDismiss?.();
    };

    const slides: CarouselSlide[] = [
        {
            key: 'first',
            header: getSlideHeader('overlappingTutorialFirstPage'),
            content: getSlideContent(localize(messages.firstSlideTitle), localize(messages.firstSlideContent)),
            announcedMessage: `${localize(messages.firstSlideTitle)}, ${localize(messages.firstSlideContent)}`,
            contentFooter: (
                <FooterContainer>
                    <ActionButton text={localize(messages.skip)} styles={skipButtonStyles} onClick={skipModal} />
                </FooterContainer>
            )
        },
        {
            key: 'second',
            header: getSlideHeader('overlappingTutorialSecondPage'),
            announcedMessage: `${localize(messages.secondSlideTitle)}, ${localize(messages.secondSlideContent)}`,
            content: getSlideContent(
                localize(messages.secondSlideTitle),
                localize(messages.secondSlideContent),
                <PrimaryButton text={localize(messages.gotIt)} onClick={skipModal} />
            )
        }
    ];

    return (
        <BasicModal
            {...props}
            styles={modalStyles}
            onDismiss={skipModal}
            shouldRenderFooter={false}
            dialogContentProps={{ styles: dialogStyles }}>
            <Carousel infiniteLoop={false} activeSlideKey={currentSlideKey} onSlideChange={setCurrentSlideKey} slides={slides} />
        </BasicModal>
    );
};
